import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {benefactorsApi} from '../api';
import {
  BenefactorModel,
  EditBenefactorEmailParams,
  EditBenefactorPhoneParams,
  EditBenefactorNamesParams,
  BenefactorPaymentsInfoModel,
  BenefactorScoringModel,
} from '../model';

type BenefactorByIdState = {
  getBenefactor: UseQueryResult<BenefactorModel, Error>;
  updateBenefactorNamesMutation: UseMutationResult<BenefactorModel, Error, EditBenefactorNamesParams>;
  updateBenefactorEmailMutation: UseMutationResult<BenefactorModel, Error, EditBenefactorEmailParams>;
  updateBenefactorPhoneMutation: UseMutationResult<BenefactorModel, Error, EditBenefactorPhoneParams>;
  deleteBenefactorMutation: UseMutationResult<void, Error, void>;
  getBenefactorPaymentsInfo: UseQueryResult<BenefactorPaymentsInfoModel[], Error>;
  refetchBenefactor: () => void;
  getBenefactorScoring: UseQueryResult<BenefactorScoringModel, Error>;
};

export const useBenefactorById = (benefactorId: string): BenefactorByIdState => {
  const queryClient = useQueryClient();

  const getBenefactor = useQuery<BenefactorModel, Error>({
    queryKey: ['benefactor', benefactorId],
    queryFn: () => benefactorsApi.getBenefactorById(benefactorId),
    enabled: !!benefactorId,
  });

  const refetchBenefactor = () => {
    queryClient.invalidateQueries({queryKey: ['benefactor', benefactorId]});
  };

  const updateBenefactorNamesMutation = useMutation<BenefactorModel, Error, EditBenefactorNamesParams>({
    mutationFn: ({benefactorId, displayName, realName}) =>
      benefactorsApi.updateBenefactorNames({benefactorId, displayName, realName}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['benefactor', benefactorId]});
      queryClient.invalidateQueries({queryKey: ['benefactors']});
    },
  });

  const updateBenefactorEmailMutation = useMutation<BenefactorModel, Error, EditBenefactorEmailParams>({
    mutationFn: ({email}) => benefactorsApi.updateBenefactorEmail({benefactorId, email}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['benefactor', benefactorId]});
      queryClient.invalidateQueries({queryKey: ['benefactors']});
    },
  });

  const updateBenefactorPhoneMutation = useMutation<BenefactorModel, Error, EditBenefactorPhoneParams>({
    mutationFn: ({phoneNumber}) => benefactorsApi.updateBenefactorPhone({benefactorId, phoneNumber}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['benefactor', benefactorId]});
      queryClient.invalidateQueries({queryKey: ['benefactors']});
    },
  });

  const deleteBenefactorMutation = useMutation<void, Error>({
    mutationFn: () => benefactorsApi.deleteBenefactor(benefactorId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['benefactors']});
    },
  });

  const getBenefactorPaymentsInfo = useQuery<BenefactorPaymentsInfoModel[], Error>({
    queryKey: ['benefactor-payments-info', benefactorId],
    queryFn: () => benefactorsApi.getBenefactorPaymentsInfo(benefactorId),
    enabled: !!benefactorId,
  });

  const getBenefactorScoring = useQuery<BenefactorScoringModel, Error>({
    queryKey: ['benefactor-scoring', benefactorId],
    queryFn: () => benefactorsApi.getBenefactorScoring(benefactorId),
    enabled: !!benefactorId,
  });

  return {
    getBenefactor,
    updateBenefactorNamesMutation,
    updateBenefactorEmailMutation,
    updateBenefactorPhoneMutation,
    deleteBenefactorMutation,
    refetchBenefactor,
    getBenefactorPaymentsInfo,
    getBenefactorScoring,
  };
};
